import { messaging } from '../utils/firebase';
import type { Notification } from '../types/user';
import { NotificationType, NotificationStatus } from '../types/user';

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/* ------------------------------------------------------------------ */
/* HELPER: FIX MESSAGE DYNAMICALLY (MINUTES TO HOURS LOGIC) */
/* ------------------------------------------------------------------ */
const fixNotificationData = (notif: any): Notification => {
  const startTimeStr = notif.data?.startTime || notif.startTime;
  
  if (startTimeStr && (notif.type === 'meeting_reminder' || notif.type === NotificationType.MEETING_REMINDER)) {
    try {
      const now = new Date();
      const [time, modifier] = startTimeStr.split(' ');
      let [hours, minutes] = time.split(':').map((n: string) => parseInt(n));

      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;

      const meetingDate = new Date();
      meetingDate.setHours(hours, minutes, 0, 0);

      const diffInMs = meetingDate.getTime() - now.getTime();
      const actualMinutesLeft = Math.round(diffInMs / 60000);

      if (actualMinutesLeft > 0) {
        let timeDisplay = "";
        
        if (actualMinutesLeft < 60) {
          timeDisplay = `${actualMinutesLeft} minutes`;
        } else {
          const hoursLeft = Math.floor(actualMinutesLeft / 60);
          const remainingMins = actualMinutesLeft % 60;
          
          if (remainingMins === 0) {
            timeDisplay = `${hoursLeft} ${hoursLeft > 1 ? 'hours' : 'hour'}`;
          } else {
            timeDisplay = `${hoursLeft} ${hoursLeft > 1 ? 'hours' : 'hour'} ${remainingMins} minutes`;
          }
        }

        notif.message = `"${notif.data?.meetingTitle || 'Meeting'}" starts in ${timeDisplay}`;
      }
    } catch (e) {
      console.error("Error calculating time:", e);
    }
  }
  return notif as Notification;
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/*  REST HELPERS */
/* ------------------------------------------------------------------ */
export async function fetchUserNotifications(userId: number): Promise<Notification[]> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/user/${userId}`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    });
    if (!res.ok) return [];
    const data = await res.json();
    console.log("📥 API FETCHED NOTIFICATIONS:", data.notifications);
    const rawNotifications = data.notifications || [];
    return rawNotifications.map((n: any) => fixNotificationData(n));
  } catch (err) {
    console.error('Error fetching notifications:', err);
    return [];
  }
}

export async function markRedisNotificationAsRead(notificationId: string): Promise<boolean> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/${notificationId}/read`, { 
      method: 'PUT', headers: { Authorization: token ? `Bearer ${token}` : '' } 
    });
    return res.ok;
  } catch { return false; }
}

export async function restoreRedisNotification(notificationId: string): Promise<boolean> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/${notificationId}/restore`, {
      method: 'PUT',
      headers: { 'Authorization': token ? `Bearer ${token}` : '' }
    });
    return res.ok;
  } catch (err) { return false; }
}

export async function restoreAllRedisNotifications(): Promise<boolean> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/all/restore`, {
      method: 'PUT',
      headers: { 'Authorization': token ? `Bearer ${token}` : '' }
    });
    return res.ok;
  } catch (err) { return false; }
}

export async function dismissRedisNotification(notificationId: string): Promise<boolean> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/${notificationId}/dismiss`, { 
      method: 'PUT', headers: { Authorization: token ? `Bearer ${token}` : '' } 
    });
    return res.ok;
  } catch { return false; }
}

export async function clearAllRedisNotifications(): Promise<boolean> {
  const token = getAuthToken();
  try {
    const res = await fetch(`${API_BASE}/api/notifications/all/dismiss`, { 
      method: 'PUT', headers: { Authorization: token ? `Bearer ${token}` : '' } 
    });
    return res.ok;
  } catch { return false; }
}

/* ------------------------------------------------------------------ */
/* FOREGROUND FIREBASE */
/* ------------------------------------------------------------------ */
export const initializeForegroundNotifications = (
  onNotificationReceived: (notification: Notification) => void
) => {
  if (typeof window === "undefined" || isIOS() || !messaging) {
    return () => {};
  }

  let unsubscribe: (() => void) | undefined;

  (async () => {
    try {
      const { onMessage } = await import("firebase/messaging");
      const processedIds = new Set<string>();

      unsubscribe = onMessage(messaging, (payload) => {
        console.log("🔔 FIREBASE REAL-TIME PAYLOAD RECEIVED:", payload);
        const data = payload.data || {};
        const notificationId = data.notificationId || `notif-${data.type}-${data.meetingId}-${Date.now()}`;

        if (processedIds.has(notificationId)) return;
        processedIds.add(notificationId);

        let rawNotification: any = {
          id: notificationId,
          type: (data.type as NotificationType) || NotificationType.SYSTEM,
          title: payload.notification?.title || data.title || "Notification",
          message: payload.notification?.body || data.message || "",
          status: NotificationStatus.UNREAD,
          userId: data.userId || "",
          meetingId: data.meetingId,
          data: { ...data },
          createdAt: new Date().toISOString()
        };

        const notification = fixNotificationData(rawNotification);
        onNotificationReceived(notification);

        const Notif = typeof window !== "undefined" && "Notification" in window ? window.Notification : null;
        if (Notif && Notif.permission === "granted") {
          showBrowserNotification(notification);
        }
      });
    } catch (err) {
      console.error("Foreground init failed:", err);
    }
  })();

  return () => { if (unsubscribe) unsubscribe(); };
};

export const showBrowserNotification = (notification: Notification) => {
  if (isIOS() || !("Notification" in window)) return;
  try {
    new Notification(notification.title, {
      body: notification.message,
      icon: "/Vela-Icon.png",
      tag: notification.id
    });
  } catch (err) {
    console.error("Browser notification error:", err);
  }
};

export const requestNotificationPermission = async (): Promise<boolean> => {
  if (typeof window === "undefined" || isIOS()) return false;
  const Notif = "Notification" in window ? window.Notification : null;
  if (!Notif) return false;
  try {
    if (Notif.permission === "granted") return true;
    if (Notif.permission !== "denied") {
      const permission = await Notif.requestPermission();
      return permission === "granted";
    }
    return false;
  } catch { return false; }
};

export const isTabActive = () => typeof document !== "undefined" && document.visibilityState === "visible";

export const formatNotificationTime = (createdAt: string): string => {
  const diff = (Date.now() - new Date(createdAt).getTime()) / 60000;
  if (diff < 1) return "Just now";
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};